import { FeatureGuard } from '@texas/components/shared/featureGuard/FeatureGuard';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  ArticleCopySourceBranch,
  ArticleCopySourceVariant,
} from '@texas/api/endpoints/articlesApi';
import { CompositionGroupView } from '@texas/components/article/variant/compositions/shared/group/CompositionGroupView';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyArticle, VariantSelection } from './useCopyArticle';

export function VariantCard({
  variant,
  isLinkedToOption,
  selection,
  branches,
}: {
  variant: ArticleCopySourceVariant;
  isLinkedToOption: boolean;
  selection?: VariantSelection;
  branches: ArticleCopySourceBranch[];
}) {
  const { t } = useTranslation();
  const { branchIds, updateOptions, updateVariant } = useCopyArticle();
  const onChange = (change: Partial<VariantSelection>) =>
    updateVariant(variant.id, change);
  const onOptionsChange = (optionIds: number[]) =>
    updateOptions(variant.id, optionIds);
  const [previewOptionId, setPreviewOptionId] = useState<number | null>(null);
  if (!selection) return null;
  const isStructurallyControlled =
    variant.isProductDevelopment || isLinkedToOption;
  const toggle = (key: keyof VariantSelection, value: boolean) => {
    if (key === 'compositionGroups' && !value) onOptionsChange([]);
    else if (key === 'compositionGroups' && value)
      onOptionsChange(
        selection.optionIds.length > 0
          ? selection.optionIds
          : variant.options.map((option) => option.id),
      );
    else if (key === 'offers' && value) {
      if (selection.optionIds.length === 0)
        onOptionsChange(variant.options.map((option) => option.id));
      onChange({ [key]: true });
    } else onChange({ [key]: value });
  };
  const options = [
    ['suppliers', t('article.copy.suppliers'), variant.supplierCount],
    ['purchaseInformation', t('article.copy.purchaseInformation'), null],
    ['salesInformation', t('article.copy.salesInformation'), null],
    ['artwork', t('article.copy.artwork'), null],
    ['photo', t('article.copy.photo'), null],
  ];
  const visibleSuppliers = variant.suppliers
    .map((supplier) => ({
      ...supplier,
      branches: supplier.branches.filter((branch) =>
        branchIds.includes(branch.branchId),
      ),
    }))
    .filter((supplier) => supplier.branches.length > 0);
  const selectedSuppliers = selection.supplierSelections;
  const setSupplier = (supplierId: number, checked: boolean) => {
    const existing = selectedSuppliers.find(
      (supplier) => supplier.supplierId === supplierId,
    );
    const next = checked
      ? existing
        ? selectedSuppliers
        : [...selectedSuppliers, { supplierId, inquiries: false }]
      : selectedSuppliers.filter(
          (supplier) => supplier.supplierId !== supplierId,
        );
    onChange({
      supplierSelections: next,
      suppliers: next.length > 0,
      inquiries: next.some((supplier) => supplier.inquiries),
      compositionGroups: checked ? true : selection.compositionGroups,
      optionIds:
        checked && selection.optionIds.length === 0
          ? variant.options.map((option) => option.id)
          : selection.optionIds,
    });
    if (checked && selection.optionIds.length === 0)
      onOptionsChange(variant.options.map((option) => option.id));
  };
  const setOption = (optionId: number, checked: boolean) => {
    const optionIds = checked
      ? [...selection.optionIds, optionId]
      : selection.optionIds.filter((id) => id !== optionId);
    if (optionIds.length === 0) {
      onOptionsChange([]);
      return;
    }
    onOptionsChange(optionIds);
  };
  const setSupplierInquiries = (supplierId: number, inquiries: boolean) => {
    const next = selectedSuppliers.map((supplier) =>
      supplier.supplierId === supplierId
        ? { ...supplier, inquiries }
        : supplier,
    );
    onChange({
      supplierSelections: next,
      inquiries: next.some((supplier) => supplier.inquiries),
    });
  };
  return (
    <Box
      border="1px solid"
      borderColor="articleManagement.divider"
      p={4}
      opacity={selection.included ? 1 : 0.6}
    >
      <Flex justify="space-between" align="center">
        <Checkbox
          isChecked={selection.included}
          isDisabled={isStructurallyControlled}
          variant={isStructurallyControlled ? 'readOnly' : undefined}
          onChange={(event) => onChange({ included: event.target.checked })}
        >
          <Text fontWeight="600">{variant.name}</Text>
        </Checkbox>
        <Badge>
          {variant.isProductDevelopment
            ? t('article.copy.productDevelopment')
            : t('article.copy.classical')}
        </Badge>
      </Flex>
      {selection.included && !variant.isProductDevelopment && (
        <Flex gap={5} mt={3} ml={6} wrap="wrap">
          {options.map(([key, label, count]) => (
            <Checkbox
              key={String(key)}
              size="sm"
              isChecked={Boolean(selection[key as keyof VariantSelection])}
              onChange={(e) =>
                toggle(key as keyof VariantSelection, e.target.checked)
              }
            >
              {label}
              {typeof count === 'number' ? ` (${count})` : ''}
            </Checkbox>
          ))}
        </Flex>
      )}
      {selection.included && variant.isProductDevelopment && (
        <Stack
          borderTop="1px solid"
          borderColor="articleManagement.divider"
          mt={3}
          mx={-4}
          mb={-4}
          spacing={0}
        >
          <CopyScopeHeader
            checked={selection.compositionGroups}
            count={variant.compositionGroupCount}
            label={t('article.copy.compositionGroups')}
            onChange={(checked) => toggle('compositionGroups', checked)}
          />
          {selection.compositionGroups && (
            <>
              {variant.options.map((option) => (
                <Flex
                  key={option.id}
                  align="center"
                  bg="articleManagement.subtleSurface"
                  borderTop="1px solid"
                  borderColor="articleManagement.divider"
                  justify="space-between"
                  minH="47px"
                  pl={12}
                  pr={5}
                  py={2}
                >
                  <Checkbox
                    isChecked={selection.optionIds.includes(option.id)}
                    onChange={(event) =>
                      setOption(option.id, event.target.checked)
                    }
                  >
                    <Text fontSize="11px" fontWeight="600">
                      {option.name}
                    </Text>
                  </Checkbox>
                  <Button
                    size="xs"
                    variant="texas-light"
                    onClick={() => setPreviewOptionId(option.id)}
                  >
                    {t('article.copy.previewOption')}
                  </Button>
                </Flex>
              ))}
              <Flex
                align="center"
                bg="articleManagement.surface"
                borderTop="1px solid"
                borderColor="articleManagement.divider"
                justify="space-between"
                pl={12}
                pr={5}
                py={3}
              >
                <Box>
                  <Text
                    fontFamily="Barlow"
                    fontSize="12px"
                    fontWeight="700"
                    letterSpacing="0.06em"
                    textTransform="uppercase"
                  >
                    {t('article.copy.supplierVersions')}
                  </Text>
                  <Text
                    color="articleManagement.mutedText"
                    fontSize="10px"
                    mt={1}
                  >
                    {t('article.copy.suppliersFollowBranches')}
                  </Text>
                </Box>
                <Text color="articleManagement.mutedText" fontSize="10px">
                  {t('article.copy.selectedCount', {
                    count: visibleSuppliers.filter((supplier) =>
                      selectedSuppliers.some(
                        (selected) => selected.supplierId === supplier.id,
                      ),
                    ).length,
                  })}
                </Text>
              </Flex>
              {visibleSuppliers.map((supplier) => {
                const selected = selectedSuppliers.find(
                  (item) => item.supplierId === supplier.id,
                );
                const versionCount = supplier.branches.reduce(
                  (total, branch) => total + branch.versionCount,
                  0,
                );
                const inquiryCount = supplier.branches.reduce(
                  (total, branch) => total + branch.inquiryCount,
                  0,
                );
                const branchLabels = supplier.branches
                  .map(
                    (supplierBranch) =>
                      branches.find(
                        (branch) => branch.id === supplierBranch.branchId,
                      )?.identifier,
                  )
                  .filter(Boolean);
                return (
                  <Flex
                    key={supplier.id}
                    align="center"
                    bg="articleManagement.subtleSurface"
                    borderTop="1px solid"
                    borderColor="articleManagement.divider"
                    minH="47px"
                    pl={12}
                    pr={5}
                    py={2}
                  >
                    <Checkbox
                      flexShrink={0}
                      isChecked={Boolean(selected)}
                      onChange={(event) =>
                        setSupplier(supplier.id, event.target.checked)
                      }
                    />
                    <Box pl={2} width="280px" flexShrink={0}>
                      <Text fontSize="11px" fontWeight="600">
                        {supplier.name}
                      </Text>
                      <Text color="articleManagement.mutedText" fontSize="9px">
                        {branchLabels.join(' · ')}
                      </Text>
                    </Box>
                    <Text fontSize="10px" width="92px" flexShrink={0}>
                      {t('article.copy.versionCount', {
                        count: versionCount,
                      })}
                    </Text>
                    <FeatureGuard hideBadge={true}>
                      <Checkbox
                        flex={1}
                        justifyContent="flex-end"
                        isChecked={Boolean(selected?.inquiries)}
                        isDisabled={!selected || inquiryCount === 0}
                        size="sm"
                        onChange={(event) =>
                          setSupplierInquiries(
                            supplier.id,
                            event.target.checked,
                          )
                        }
                      >
                        {inquiryCount > 0
                          ? t('article.copy.inquiryCount', {
                              count: inquiryCount,
                            })
                          : t('article.copy.noInquiry')}
                      </Checkbox>
                    </FeatureGuard>
                  </Flex>
                );
              })}
              <FeatureGuard hideBadge={true}>
                <CopyScopeHeader
                  checked={selection.offers}
                  count={variant.offerCount}
                  label={t('article.copy.offers')}
                  onChange={(checked) => toggle('offers', checked)}
                />
              </FeatureGuard>
            </>
          )}
        </Stack>
      )}
      <Modal
        isCentered={true}
        isOpen={previewOptionId !== null}
        onClose={() => setPreviewOptionId(null)}
      >
        <ModalOverlay />
        <ModalContent maxW="fit-content" width="auto">
          <ModalHeader>{t('article.copy.previewOption')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={5}>
            {previewOptionId !== null && (
              <CompositionGroupView
                componentSettings={{
                  expandedDefault: true,
                  addMaterials: 'hidden',
                  materialsMode: 'read-only',
                  removeMaterials: 'hidden',
                  editImage: 'hidden',
                  dimensionMode: 'read-only',
                  compositionArchiveRestore: 'hidden',
                }}
                variantId={variant.id}
                compositionGroupId={previewOptionId}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
function CopyScopeHeader({
  checked,
  count,
  label,
  onChange,
}: {
  checked: boolean;
  count: number;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Flex align="center" justify="space-between" pl={12} pr={5} py={3}>
      <Checkbox
        isChecked={checked}
        onChange={(event) => onChange(event.target.checked)}
      >
        <Text
          fontFamily="Barlow"
          fontSize="12px"
          fontWeight="700"
          letterSpacing="0.06em"
          textTransform="uppercase"
        >
          {label} <Badge borderRadius={0}>{count}</Badge>
        </Text>
      </Checkbox>
    </Flex>
  );
}
